'use client'

import FirstLogo from '@/public/logo/first.svg'
import { ArrowRight, ChevronLeft, Menu, User, X } from 'lucide-react'
import { Button } from '../ui/button'
import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { Link } from '@/i18n/navigation'
import { City, Country, Header as HeaderType } from '@/payload-types'
import { Language } from '@/types/language.types'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion'
import LanguageSwitcher from './LanguageSwitcher'
import { MenuLinkWrapper } from '../common/LinkWrapper'
import { useTranslations } from 'next-intl'
import { displayAsciArt } from '@/lib/hbTerminal'
import CartSheet from './CartSheet'

const itemVariants = {
  closed: {
    x: -10,
    opacity: 0,
    filter: 'blur(4px)',
  },
  open: {
    x: 0,
    opacity: 1,
    filter: 'blur(0px)',
  },
}

const navVariants = {
  closed: {
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1,
      delayChildren: 0.1,
    },
  },
  open: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
}

const Header = ({
  header,
  locale,
  cities,
  isLoggedIn,
  name,
}: {
  header: HeaderType
  locale: Language
  cities: City[]
  isLoggedIn: boolean
  name?: string
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [subMenuOpen, setSubMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  const t = useTranslations()

  const citiesByCountry = cities.reduce(
    (acc, city) => {
      const country = typeof city.country === 'number' ? null : city.country

      if (!country) return acc

      if (!acc[country.id]) {
        acc[country.id] = {
          country,
          cities: [],
        }
      }

      acc[country.id].cities.push(city)

      return acc
    },
    {} as Record<
      number,
      {
        country: Country
        cities: typeof cities
      }
    >,
  )

  useEffect(() => {
    if (menuOpen) {
      // Prevent scroll
      document.body.style.overflow = 'hidden'

      // The menu covers the page, so keep keyboard and screen reader users out of the content behind it
      const covered = document.querySelectorAll('main, footer')
      covered.forEach((el) => el.setAttribute('inert', ''))

      // Handle escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          if (subMenuOpen) {
            setSubMenuOpen(false)
          } else {
            setMenuOpen(false)
            menuButtonRef.current?.focus()
          }
        }
      }

      document.addEventListener('keydown', handleEscape)

      // Cleanup function to restore scroll and remove event listener
      return () => {
        document.body.style.overflow = ''
        covered.forEach((el) => el.removeAttribute('inert'))
        document.removeEventListener('keydown', handleEscape)
      }
    }
  }, [menuOpen, subMenuOpen])

  useEffect(() => {
    displayAsciArt()
  }, [])

  return (
    <header className='h-(--header-height)'>
      <nav aria-label={t('main_navigation')} className={'fixed top-0 left-0 z-50 w-full'}>
        <div className='bg-background flex h-(--header-height) items-center'>
          {/*<div className='w-full container-max mx-auto px-4 flex justify-between items-center'>*/}
          <div className='container-max mx-auto flex w-full grid-cols-12 items-center justify-between px-4 lg:grid'>
            <Link
              href='/'
              onClick={() => setMenuOpen(false)}
              aria-label={`First Hotels – ${t('home')}`}
              className='col-span-3'
            >
              <FirstLogo className='w-24' />
            </Link>

            {header.navItemsDesktop && (
              <div className='col-span-6 hidden justify-center space-x-4 lg:flex'>
                {header.navItemsDesktop.map((navItem) => (
                  <div key={navItem.id} onClick={() => setMenuOpen(false)}>
                    <MenuLinkWrapper
                      link={navItem.link}
                      className='block w-full py-4 transition-all md:hover:underline'
                    >
                      <h3 className='text-base font-normal md:text-xl'>{navItem.link.label}</h3>
                    </MenuLinkWrapper>
                  </div>
                ))}
              </div>
            )}

            <div className='col-span-3 flex items-center justify-end space-x-4'>
              <CartSheet />

              <div className='flex items-center space-x-4'>
                {isLoggedIn ? (
                  <div className='group relative'>
                    <Link
                      href={'/account'}
                      aria-label={name ? `${t('my_account')}: ${name}` : t('my_account')}
                      className='border-secondary text-accent-foreground flex size-10 items-center justify-center rounded-full border bg-[#F3EEE2] text-sm font-medium transition-opacity hover:opacity-50'
                    >
                      {name ? (
                        name
                          .split(' ')
                          .filter(Boolean)
                          .map((w, i, a) => (i === 0 || i === a.length - 1 ? w[0] : null))
                          .filter(Boolean)
                          .join('')
                          .toUpperCase()
                      ) : (
                        <User className='size-5' />
                      )}
                    </Link>
                    {name && (
                      <div className='pointer-events-none absolute top-full right-1/2 mt-2 translate-x-1/2 rounded-md bg-white px-3 py-1.5 text-sm whitespace-nowrap opacity-0 shadow-md transition-opacity group-hover:opacity-100'>
                        {name}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <Link
                      href={'/first-member'}
                      className='hover:bg-border hidden rounded-full border px-4 py-2 whitespace-nowrap transition-colors md:block'
                    >
                      {t('become_a_member')}
                    </Link>
                    <Link
                      href={'/login'}
                      className='border-secondary text-accent-foreground rounded-full border bg-[#F3EEE2] px-4 py-2 whitespace-nowrap transition-opacity hover:opacity-50'
                    >
                      {t('log_in')}
                    </Link>
                  </>
                )}
              </div>

              <Button
                ref={menuButtonRef}
                size={'icon'}
                variant={'ghost'}
                className={cn({
                  'bg-stone-200': menuOpen,
                })}
                onClick={() => setMenuOpen((prev) => !prev)}
                aria-label={t('menu')}
                aria-expanded={menuOpen}
              >
                {menuOpen ? (
                  <X className='size-6' strokeWidth={1.5} />
                ) : (
                  <Menu className='size-6' strokeWidth={1.5} />
                )}
              </Button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              key='menu'
              className='bg-sidebar relative flex h-[calc(100vh_-_var(--header-height))] w-screen flex-col'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className='container-max mx-auto h-full w-full overflow-y-auto p-4'>
                {!subMenuOpen ? (
                  <div key='menu-items'>
                    <motion.div
                      initial='closed'
                      animate='open'
                      exit='closed'
                      variants={navVariants}
                      className='[&>*:not(:last-child)]:border-b'
                    >
                      <motion.button
                        key={'hotels-apartments'}
                        variants={itemVariants}
                        transition={{ ease: 'easeOut' }}
                        className='flex w-full justify-between py-4 text-xl transition-opacity duration-300 hover:opacity-50 md:hover:underline'
                        onClick={() => setSubMenuOpen(true)}
                      >
                        <h3>{t('hotels_and_apartments')}</h3>
                        <ArrowRight className='text-muted-foreground size-6' />
                      </motion.button>

                      {header.navItems &&
                        header.navItems.map((navItem, i) => (
                          <motion.div
                            key={navItem.id}
                            variants={itemVariants}
                            transition={{ ease: 'easeOut' }}
                            className='w-full text-xl font-medium lg:text-2xl'
                            onClick={() => setMenuOpen(false)}
                          >
                            <MenuLinkWrapper
                              link={navItem.link}
                              className='block w-full py-4 md:hover:underline'
                            >
                              {navItem.link.label}
                            </MenuLinkWrapper>
                          </motion.div>
                        ))}

                      <motion.div
                        key={'becoma-a-member'}
                        variants={itemVariants}
                        transition={{ ease: 'easeOut' }}
                        className='w-full text-xl font-medium md:hidden lg:text-2xl'
                        onClick={() => setMenuOpen(false)}
                      >
                        <Link
                          href={'/first-member'}
                          className='block w-full py-4 md:hover:underline'
                        >
                          {t('become_a_member')}
                        </Link>
                      </motion.div>
                    </motion.div>

                    <motion.div
                      initial='closed'
                      animate='open'
                      exit='closed'
                      variants={navVariants}
                      className='pt-8'
                    >
                      {header.navItemsSmall &&
                        header.navItemsSmall.map((navItem, i) => (
                          <motion.h3
                            key={i}
                            variants={itemVariants}
                            transition={{ ease: 'easeOut' }}
                            className='text-muted-foreground py-2 text-base font-normal! lg:text-xl'
                            onClick={() => setMenuOpen(false)}
                          >
                            <MenuLinkWrapper link={navItem.link}>
                              {navItem.link.label}
                            </MenuLinkWrapper>
                          </motion.h3>
                        ))}
                    </motion.div>
                  </div>
                ) : (
                  <motion.div
                    key={'sub-menu'}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className='space-y-4'
                  >
                    <Button variant={'grey'} onClick={() => setSubMenuOpen(false)}>
                      <ChevronLeft />
                      <p>{t('back')}</p>
                    </Button>

                    <p className='text-stone-500'>{t('hotels_and_apartments')}</p>

                    <motion.div
                      initial='closed'
                      animate='open'
                      exit='closed'
                      variants={navVariants}
                    >
                      <Accordion
                        type='single'
                        collapsible
                        className='[&>*]:border-b [&>*:first-child]:pt-0'
                      >
                        {Object.values(citiesByCountry).map(({ country, cities }) => {
                          return (
                            <AccordionItem key={country.id} value={country.title} className='py-4'>
                              <AccordionTrigger>
                                <h3>{country.title}</h3>
                              </AccordionTrigger>
                              <AccordionContent className='px-0'>
                                <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 md:gap-4 lg:grid-cols-5'>
                                  {cities.map((city) => {
                                    if (typeof city === 'number') return null

                                    if (
                                      city.hotels?.docs?.length === 1 &&
                                      typeof city.hotels.docs[0] === 'object'
                                    ) {
                                      return (
                                        <Link
                                          key={city.id}
                                          href={{
                                            pathname: '/hotels/[country]/[city]/[hotel]',
                                            params: {
                                              country: country.slug as string,
                                              city: city.slug as string,
                                              hotel: city.hotels.docs[0].slug as string,
                                            },
                                          }}
                                          className='w-fit md:hover:underline md:hover:opacity-50'
                                          onClick={() => setMenuOpen(false)}
                                        >
                                          {city.title} ({city.hotels?.docs?.length})
                                        </Link>
                                      )
                                    } else {
                                      return (
                                        <Link
                                          key={city.id}
                                          href={{
                                            pathname: '/hotels/[country]/[city]',
                                            params: {
                                              country: country.slug as string,
                                              city: city.slug as string,
                                            },
                                          }}
                                          className='w-fit md:hover:underline md:hover:opacity-50'
                                          onClick={() => setMenuOpen(false)}
                                        >
                                          {city.title} ({city.hotels?.docs?.length})
                                        </Link>
                                      )
                                    }
                                  })}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          )
                        })}
                      </Accordion>
                    </motion.div>
                  </motion.div>
                )}

                <div
                  className={cn('left-0 mt-6 flex w-full items-center justify-center', {
                    hidden: subMenuOpen,
                  })}
                >
                  <LanguageSwitcher currentLocale={locale} dark />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  )
}

export default Header
